import axios, {AxiosResponse} from 'axios'
import {AxiosAuthRefreshRequestConfig} from 'axios-auth-refresh'
import qs from 'qs'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {AuthModel, UserModel, UserRegistrationResultModel} from './_models'

const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL

export const GET_USER_BY_ACCESSTOKEN_URL = `${AUTH_API_URL}/verify_token`
export const LOGIN_URL = `${AUTH_API_URL}/token`
export const REGISTER_URL = `${AUTH_API_URL}/register`
export const FORGOT_PASSWORD_URL = `${AUTH_API_URL}/forgot_password`
export const VERIFY_RESET_PASSWORD_URL = `${AUTH_API_URL}/verify_reset_password`
export const RESET_PASSWORD_URL = `${AUTH_API_URL}/reset_password`
export const VERIFY_CONFIRM_EMAIL_URL = `${AUTH_API_URL}/verify_confirm_email`
export const CONFIRM_EMAIL_URL = `${AUTH_API_URL}/confirm_email`
export const RESEND_EMAIL_CONFIRMATION_URL = `${AUTH_API_URL}/resend_email_confirmation`
export const UPDATE_PASSWORD_URL = `${AUTH_API_URL}/change_password`
export const SEND_CODE_URL = `${AUTH_API_URL}/send_code`

/**
 * Login using the provided username and password and returns an AuthModel with the access token and refresh token
 */
export function login(username: string, password: string) {
  var config: AxiosAuthRefreshRequestConfig = {
    headers: {'Content-Type': 'application/x-www-form-urlencoded'},
    skipAuthRefresh: true,
  }

  return axios.post<AuthModel>(
    LOGIN_URL,
    qs.stringify({
      grant_type: 'password',
      username,
      password,
    }),
    config
  )
}

export function twoFactorLogin(username: string, password: string, code: string, provider: string, rememberMe: boolean = true) {
  var config: AxiosAuthRefreshRequestConfig = {
    headers: {'Content-Type': 'application/x-www-form-urlencoded'},
    skipAuthRefresh: true,
  }

  return axios.post<AuthModel>(
    `${LOGIN_URL}${code && provider ? `?code=${code}&provider=${provider}&rememberMe=${rememberMe}` : ''}`,
    qs.stringify({
      grant_type: 'password',
      username,
      password,
    }),
    config
  )
}

/**
 * Get a new access token using the provided refresh token
 */
export function reauthWithRefreshToken(refreshToken: string) {
  var config: AxiosAuthRefreshRequestConfig = {
    headers: {'Content-Type': 'application/x-www-form-urlencoded'},
    skipAuthRefresh: true,
  }

  return axios.post<AuthModel>(
    LOGIN_URL,
    qs.stringify({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    }),
    config
  )
}

/** Register a new user account */
export function register(username: string, email: string, displayname: string, password: string, password_confirmation: string) {
  return axios.post<UserRegistrationResultModel>(REGISTER_URL, {
    username,
    email,
    displayname,
    password,
    password_confirmation,
    confirmEmailUrl: `${window.location.origin}${toAbsoluteUrl('/auth/confirm-email')}`, // callback URL to confirm email
  })
}

/** Request a password reset.  Server should return { result: boolean } object */
export function requestPasswordReset(email: string) {
  return axios.post<{result: boolean}>(FORGOT_PASSWORD_URL, {
    email,
    callbackUrl: `${window.location.origin}${toAbsoluteUrl('/auth/reset-password')}`, // callback URL to reset password
  })
}

export function verifyResetPassword(userId: number, token: string) {
  return axios.post<boolean>(VERIFY_RESET_PASSWORD_URL, {userId, token}).then((response: AxiosResponse<boolean>) => response.data)
}

/** Perform a password reset.  Server should return { result: boolean } object */
export function resetPassword(userId: number, token: string, newPassword: string, newPasswordConfirmation: string) {
  return axios.post<{result: boolean}>(RESET_PASSWORD_URL, {
    userId,
    token,
    newPassword,
    newPasswordConfirmation,
  })
}

/** Change Password.  Server should return { result: boolean } object */
export function updatePassword(currentPassword: string, newPassword: string, newPasswordConfirmation: string) {
  return axios.post<{result: boolean}>(UPDATE_PASSWORD_URL, {
    currentPassword,
    newPassword,
    newPasswordConfirmation,
  })
}

/** Get the current user's info */
export function getCurrentUserInfo() {
  return axios.get<UserModel>(GET_USER_BY_ACCESSTOKEN_URL)
}

/** Verify the validity of the provided token */
export function verifyConfirmEmail(userId: number, token: string) {
  return axios.post<string>(VERIFY_CONFIRM_EMAIL_URL, {userId, token}).then((response: AxiosResponse<string>) => response.data)
}

/** Confirm a user's email using the provided token */
export function confirmEmail(userId: number, token: string, newPassword: string, newPasswordConfirmation: string) {
  return axios.post(CONFIRM_EMAIL_URL, {userId, token, newPassword, newPasswordConfirmation})
}

/** Request a new email confirmation email */
export function resendEmailConfirmation(email: string) {
  return axios.post<{result: boolean}>(RESEND_EMAIL_CONFIRMATION_URL, {
    email,
    callbackUrl: `${window.location.origin}${toAbsoluteUrl('/auth/confirm-email')}`, // callback URL to confirm email
  })
}

/** Generate and send a two-factor authentication code to the current user */
export function sendCode(provider: string) {
  var config: AxiosAuthRefreshRequestConfig = {
    skipAuthRefresh: true,
  }

  return axios.post(SEND_CODE_URL, {provider}, config)
}
